import { useForm, useWatch } from "react-hook-form";
import { object, string, number, boolean, InferType } from "yup";
import { useAppTranslation } from "@hooks/useAppTranslation";
import { DetailedSection } from "@typeDefinitions";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEducationalModalContext } from "@views/dietician/EducationalMaterials/_hooks/useEducationalMaterialsContext";
import {
  EducationalMaterialsTabs,
  Language,
  SectionCategory,
} from "@views/dietician/EducationalMaterials/EducationalMaterialsView.types";
import {
  useCreateSectionMutation,
  useFetchProgramSectionQuery,
  useFetchSectionQuery,
  useUpdateProgramSectionMutation,
  useUpdateSectionMutation,
} from "./queries";
import { FetchSectionResponse, LangDto } from "@client";
import { useDebounce } from "react-use";
import isEqual from "lodash/isEqual";
import { Dispatch, SetStateAction } from "react";
import { useQueryParams } from "./queries/useQueryParams";
import { ProgramSectionDto } from "@client";

const mapSectionToFormSchema = (
  section: FetchSectionResponse,
  currentLanguageDTO: LangDto,
): SectionFormSchema => {
  return {
    language:
      section.titleEn && currentLanguageDTO === LangDto.EN
        ? Language.EN
        : Language.PL,
    title: section.title,
    titleEn: section.titleEn ?? null,
    content: section.content,
    contentEn: section.contentEn ?? null,
    description_pl: section.descriptionPl ?? "",
    description_en: section.descriptionEn ?? null,
    imageId: section.image?.id ?? null,
    imageUrl: section.image?.url ?? "",
    categoryId: section.programSectionCategory.id,
    reviewed: section.reviewed,
    coverType: section.image?.url ? "custom" : "template",
  };
};

const mapProgramSectionToFormSchema = (
  programSection: ProgramSectionDto,
  defaultValues: SectionFormSchema,
): SectionFormSchema => {
  return {
    ...defaultValues,
    title: programSection.title ?? "",
    titleEn: programSection.titleEn ?? null,
    content: programSection.content ?? "",
    contentEn: programSection.contentEn ?? null,
    description_pl: programSection.description ?? "",
    description_en: programSection.descriptionEn ?? null,
    imageId: programSection.image?.id ?? null,
    imageUrl: programSection.image?.url ?? "",
    coverType: programSection.image?.url ? "custom" : "template",
  };
};

const formSchema = object().shape({
  language: string().defined(),
  title: string()
    .nullable()
    .when("language", {
      is: Language.PL,
      then: schema => schema.required("Title in Polish is required"),
      otherwise: schema => schema.nullable(),
    }),
  titleEn: string()
    .nullable()
    .when("language", {
      is: Language.EN,
      then: schema => schema.required("Title in English is required"),
      otherwise: schema => schema.nullable(),
    }),
  content: string()
    .nullable()
    .when("language", {
      is: Language.PL,
      then: schema => schema.required("Content in Polish is required"),
      otherwise: schema => schema.nullable(),
    }),
  contentEn: string()
    .nullable()
    .when("language", {
      is: Language.EN,
      then: schema => schema.required("Content in English is required"),
      otherwise: schema => schema.nullable(),
    }),
  description_pl: string().default(""),
  description_en: string().nullable().default(""),
  imageId: number().nullable().default(null),
  imageUrl: string().default(""),
  reviewed: boolean().default(false),
  categoryId: number().default(SectionCategory.OTHERS),
  coverType: string(),
});

export type SectionFormSchema = InferType<typeof formSchema>;

interface UseSectionFormOptions {
  defaultSection?: DetailedSection;
  onSave?: Dispatch<SetStateAction<boolean>>;
  programId?: number;
}

export function useSectionForm(options?: UseSectionFormOptions) {
  const { onSave, programId } = options || {};
  const { currentLanguageDTO } = useAppTranslation();

  const { get } = useQueryParams("tab");

  const { tab } = get<{ tab: EducationalMaterialsTabs }>();

  const { mutateAsync: createSectionMutation } = useCreateSectionMutation();
  const { mutateAsync: updateSectionMutation } = useUpdateSectionMutation();
  const { mutateAsync: updateProgramSectionMutation } =
    useUpdateProgramSectionMutation();

  const { sectionId, materialId, updateMaterialId } =
    useEducationalModalContext();

  const { section } = useFetchSectionQuery(Number(materialId), {
    enabled: !!materialId && tab === EducationalMaterialsTabs.TEXT_MATERIALS,
  });

  const { data: programSection } = useFetchProgramSectionQuery(
    programId?.toString() ?? "",
    materialId?.toString() ?? "",
    !!programId && !!materialId,
  );

  const defaultValues = {
    language: currentLanguageDTO,
    title: "",
    titleEn: "",
    content: "",
    description_pl: "",
    description_en: "",
    contentEn: "",
    imageId: null,
    imageUrl: "",
    reviewed: false,
    categoryId: SectionCategory.OTHERS,
    coverType: "template",
  };

  const composedData =
    programId && programSection
      ? mapProgramSectionToFormSchema(programSection, defaultValues)
      : materialId && section
      ? mapSectionToFormSchema(section.data, currentLanguageDTO)
      : {
          ...defaultValues,
          programSectionCategoryId: sectionId ?? defaultValues.categoryId,
        };

  const form = useForm<SectionFormSchema>({
    defaultValues,
    mode: "onChange",
    values: composedData,
    resolver: yupResolver(formSchema),
    reValidateMode: "onChange",
  });

  const formData = useWatch({ control: form.control });

  const { isDirty, isValid } = form.formState;

  const handleCreateSection = async () => {
    if (isDirty && !materialId && isValid) {
      try {
        const newMaterial = await createSectionMutation({
          title: formData.title || formData.titleEn || "",
          titleEn: formData.titleEn ?? null,
          content: formData.content || formData.contentEn || "",
          contentEn: formData.contentEn ?? null,
          description_pl: formData.description_pl ?? "",
          description_en: formData.description_en ?? null,
          imageId:
            formData.imageId && formData.coverType === "custom"
              ? formData.imageId
              : null,
          programSectionCategoryId:
            formData.categoryId ?? SectionCategory.OTHERS,
          reviewed: formData.reviewed,
        });

        if (newMaterial) {
          updateMaterialId(newMaterial.data.id);
          onSave?.(true);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handleUpdateSection = async () => {
    if (
      materialId &&
      isValid &&
      !isEqual(formData, composedData) &&
      programId
    ) {
      await updateProgramSectionMutation({
        sectionId: Number(materialId),
        programId: programId,
        payload: {
          title: formData.title ?? "",
          titleEn: formData.titleEn ?? "",
          content: formData.content ?? "",
          contentEn: formData.contentEn ?? "",
          description: formData.description_pl ?? "",
          descriptionEn: formData.description_en ?? "",
          imageId: formData.imageId ?? null,
        },
      });
      onSave?.(true);
    }

    if (
      materialId &&
      isValid &&
      !isEqual(formData, composedData) &&
      !programId
    ) {
      try {
        await updateSectionMutation({
          id: Number(materialId),
          input: {
            title: formData.title || formData.titleEn || "",
            titleEn: formData.titleEn ?? null,
            content: formData.content || formData.contentEn || "",
            contentEn: formData.contentEn ?? null,
            description_pl: formData.description_pl ?? "",
            description_en: formData.description_en ?? null,
            imageId:
              formData.imageId && formData.coverType === "custom"
                ? formData.imageId
                : null,
            programSectionCategoryId:
              formData.categoryId ?? SectionCategory.OTHERS,
            reviewed: formData.reviewed,
          },
        });
        onSave?.(true);
      } catch (error) {
        console.error(error);
      }
    }
  };

  useDebounce(
    () => {
      if (!materialId) {
        handleCreateSection();
      } else {
        handleUpdateSection();
      }
    },
    1000,
    [formData, isDirty, isValid, materialId],
  );

  return form;
}
